import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { disconnectSocket, reconnectSocketWithLatestToken } from '../services/socket-io';

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const keepAliveInterval = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit('ping');
      }
    }, 25000); 

    return () => {
      clearInterval(keepAliveInterval);
    };
  }, [socket]);

  const syncSocketFromToken = useCallback(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      disconnectSocket();
      setSocket(null);
      setIsConnected(false);
      return;
    }

    const newSocket = reconnectSocketWithLatestToken();
    setSocket(newSocket);
    setIsConnected(Boolean(newSocket?.connected));
  }, []);

  useEffect(() => {
    syncSocketFromToken();

    const onAuthChanged = () => {
      syncSocketFromToken();
    };

    const onStorage = (event) => {
      if (event.key === 'token') {
        syncSocketFromToken();
      }
    };

    window.addEventListener('auth:changed', onAuthChanged);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('auth:changed', onAuthChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, [syncSocketFromToken]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleConnect = () => {
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  const emit = useCallback((event, payload) => {
    if (!socket || !socket.connected) return false;
    socket.emit(event, payload);
    return true;
  }, [socket]);

  const on = useCallback((event, handler) => {
    if (!socket) return false;
    socket.on(event, handler);
    return true;
  }, [socket]);

  const off = useCallback((event, handler) => {
    if (!socket) return false;
    socket.off(event, handler);
    return true;
  }, [socket]);

  const value = useMemo(
    () => ({
      socket,
      connected: isConnected,
      isConnected,
      emit,
      on,
      off,
      reconnect: syncSocketFromToken,
    }),
    [socket, isConnected, emit, on, off, syncSocketFromToken]
  );

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket deve ser usado dentro de um SocketProvider');
  }
  return context;
};
